const post = {
  meta: {
    slug: "sba-7a-vs-sba-504",
    title: "SBA 7(a) vs. SBA 504: Which Loan Program Fits Your Deal?",
    excerpt:
      "Two SBA programs, two very different structures. Get this one right and you save six figures over the life of the deal. Get it wrong and you're refinancing in two years.",
    date: "2025-12-22",
    readTime: "6 min read",
    category: "SBA",
    image:
      "https://images.unsplash.com/photo-1758866555610-c44c609ae88b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBicnV0YWxpc3QlMjBhcmNoaXRlY3R1cmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODQ1MDcwODN8MA&ixlib=rb-4.1.0&q=85",
  },
  body: `
## Two programs, one agency

The **Small Business Administration** doesn't lend money. It *guarantees* loans made by private banks. Two of the SBA's programs dominate small-business finance: the **7(a)** and the **504**. They look similar on a flyer — both are SBA-backed, both offer great rates, both go up to $5 million per project. But their structures are entirely different, and picking the wrong one costs you real money.

## SBA 7(a): the general-purpose loan

The 7(a) is the SBA's flagship program. Think of it as a **flexible working-capital-friendly term loan**, partially guaranteed by the SBA.

Key features:
- **Amount:** Up to $5 million
- **Structure:** One loan, one bank, one payment
- **Rate:** Variable, typically Prime + 2.75–4.75%. In today's Prime environment, that's roughly 11–13% APR.
- **Term:** Up to 10 years for working capital / equipment; up to 25 years for real estate
- **Down payment:** Typically 10% for most uses; 15%+ for change-of-ownership deals
- **Use of funds:** Almost anything legal — working capital, inventory, equipment, business acquisition, real estate, partner buyout, refinance
- **Timeline:** 30–75 days

**Best when:** you want maximum flexibility, you're financing a mix of things (business + equipment + working capital), or the deal isn't primarily real estate.

## SBA 504: the real-estate & heavy-equipment loan

The 504 is a **specialized program for fixed assets** — commercial real estate and long-life equipment. It's structured very differently from a 7(a).

A 504 deal is actually **two loans stacked** on the same purchase:

1. **First mortgage** — from a private bank, covers 50% of the project, typical commercial rate (usually 6.5–8% today, fixed for 5–10 years then reset)
2. **Second mortgage** — from a Certified Development Company (CDC), backed by an SBA-guaranteed debenture, covers 40% of the project, **fixed for 20 or 25 years** at an SBA-set below-market rate (typically 6.5–7.5% today, fully fixed)
3. **Your down payment** — the remaining 10%

Key features:
- **Amount:** SBA portion up to $5.5M ($5M standard, $5.5M for manufacturing/green projects). Total project size effectively unlimited because bank portion has no cap.
- **Use of funds:** Owner-occupied commercial real estate (51%+ owner-occupied) OR long-life machinery/equipment (10+ year useful life). **Not for working capital, inventory, or general business purposes.**
- **Down payment:** 10% (12% for special-purpose properties; 15%+ for startups)
- **Term:** 20 or 25 years, fully amortized, on the SBA/CDC portion
- **Timeline:** 60–90 days (two closings — bank + CDC)

**Best when:** you're buying commercial real estate you'll occupy, or heavy long-life equipment.

## The head-to-head that actually matters

Let's price a real deal. You're buying a $1,000,000 building for your business.

### Option A: SBA 7(a) for the full purchase
- Loan amount: $900,000 (you put 10% down)
- Rate: 12% variable
- Term: 25 years
- **Monthly payment: ~$9,480**
- Full rate exposure over 25 years — if Prime rises 2 points, your payment rises with it

### Option B: SBA 504 with 50/40/10 split
- First mortgage: $500,000 from a bank at 7.5%, 25-year amortization
- Second mortgage (CDC/SBA): $400,000 at 6.8% fixed for 25 years
- Your down: $100,000
- **Combined monthly payment: ~$6,462**
- The 504 portion is fixed for 25 years — your payment on that piece never changes

**Difference: about $3,000/month. Over 25 years, that's $900,000 in cash-flow savings.**

That's why for real estate deals of this size, a good broker will push you hard toward a 504.

## Where 7(a) still wins on real estate

The 504 is not always the right answer for real estate. The 7(a) can be better when:

- **The deal is small** (under ~$500K total) — the 504's two-closing structure adds fees and complexity that don't pay back on smaller deals
- **You need working capital rolled in** — the 504 can't fund working capital; a 7(a) can bundle real estate + working capital into one loan
- **Speed matters** — 7(a) closes in 45 days; 504 typically takes 75 days
- **You have a 7(a)-only bank relationship** — some banks do a lot of 7(a) but no 504, and the borrower's advocate is the underwriter, not the program

## Where 504 clearly wins

- Owner-occupied real estate purchases of $500K or more
- Heavy machinery / equipment purchases with 10+ year useful lives
- Any situation where you want a large portion of your debt fixed for 25 years and immune to future rate hikes
- Green/energy-efficient building purchases (which qualify for enhanced 504 limits)

## The five-second decision tree

- **Buying real estate you'll occupy? Deal over $500K?** → Start with 504.
- **Buying real estate + also need working capital or inventory?** → 7(a).
- **Buying long-life equipment (10+ year life)?** → 504 usually wins.
- **Buying a business, refinancing debt, or funding general operations?** → 7(a).
- **Not sure or the deal has multiple components?** → That's what a broker who runs both programs is for.

At Steady Path Funding, we place both. We'll model both structures on the same deal, hand you the numbers side by side, and let the math decide. Because on a 25-year deal, the difference between the right program and the wrong one is a career's worth of interest.
`,
};

export default post;
